import React from 'react';
import {
  CommandSelect,
  CommandSelectTrigger,
  CommandSelectValue,
  CommandSelectContent,
  CommandSelectEmpty,
  CommandSelectLoading,
  CommandSelectItem,
  CommandSelectAll,
  useCommandSelectSearch,
} from '../../Analytics/components/filters-form/command-select';

// Types for Ad Campaigns
interface AdCampaign {
  id: string;
  name: string;
  hasPaidPartnershipAds?: boolean;
}

// Combined type for display
interface AdCampaignsSelectProps {
  children: React.ReactNode;
}

export const AdCampaignsSelect: React.FC<AdCampaignsSelectProps> = ({ children }) => {
  return <div className="relative">{children}</div>;
};

// Content component that can access CommandSelect context
const AdCampaignsContent: React.FC<{
  adCampaigns: AdCampaign[];
  isLoading: boolean;
  selectedCampaignIds: string[];
  isUpdating: boolean;
  handleSelectCampaign: (campaignId: string) => void;
  updateSelection: (selection: string[]) => void;
}> = ({ adCampaigns, isLoading, selectedCampaignIds, isUpdating, handleSelectCampaign, updateSelection }) => {
  // Get search query for filtering (now inside CommandSelect context)
  const searchQuery = useCommandSelectSearch();

  // Filter campaigns based on search query
  const filteredCampaigns = React.useMemo(() => {
    if (!searchQuery || searchQuery.trim() === '') {
      return adCampaigns;
    }

    const query = searchQuery.toLowerCase().trim();
    return adCampaigns.filter(
      (campaign) => campaign.name.toLowerCase().includes(query) || campaign.id.toLowerCase().includes(query),
    );
  }, [adCampaigns, searchQuery]);

  // Combine filtered campaigns with selected campaigns (similar to MembersOptions pattern)
  const displayCampaigns = React.useMemo(() => {
    const campaignMap = new Map<string, AdCampaign & { state: 'filtered' | 'selected' }>();

    // Add filtered campaigns
    filteredCampaigns.forEach((campaign) => {
      campaignMap.set(campaign.id, { ...campaign, state: 'filtered' });
    });

    // Add selected campaigns that might not be in filtered results
    selectedCampaignIds.forEach((campaignId) => {
      if (!campaignMap.has(campaignId)) {
        const selectedCampaign = adCampaigns.find((camp) => camp.id === campaignId);
        if (selectedCampaign) {
          campaignMap.set(campaignId, { ...selectedCampaign, state: 'selected' });
        }
      }
    });

    return Array.from(campaignMap.values());
  }, [filteredCampaigns, selectedCampaignIds, adCampaigns]);

  // Sort campaigns: selected first, then filtered results
  const sortedCampaigns = React.useMemo(() => {
    return [...displayCampaigns].sort((a, b) => {
      const aSelected = selectedCampaignIds.includes(a.id);
      const bSelected = selectedCampaignIds.includes(b.id);

      // Selected items go to the top
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // If both selected or both unselected, maintain original order
      return 0;
    });
  }, [displayCampaigns, selectedCampaignIds]);

  // Get visible campaign IDs for Select All logic
  const visibleCampaignIds = React.useMemo(() => {
    return filteredCampaigns.map((campaign) => campaign.id);
  }, [filteredCampaigns]);

  // Get selected IDs that are currently visible
  const selectedVisibleIds = React.useMemo(() => {
    return selectedCampaignIds.filter((id) => visibleCampaignIds.includes(id));
  }, [selectedCampaignIds, visibleCampaignIds]);

  // Create a wrapped handleSelectAll that has access to filteredCampaigns
  const wrappedHandleSelectAll = React.useCallback(() => {
    if (isUpdating || isLoading) {
      return;
    }

    let newSelection: string[];

    if (selectedVisibleIds.length === visibleCampaignIds.length && visibleCampaignIds.length > 0) {
      // Deselect all visible campaigns (preserve selections from other search contexts)
      newSelection = selectedCampaignIds.filter((id) => !visibleCampaignIds.includes(id));
    } else {
      // Select all visible campaigns (preserve existing selections)
      newSelection = Array.from(new Set([...selectedCampaignIds, ...visibleCampaignIds]));
    }

    updateSelection(newSelection);
  }, [visibleCampaignIds, selectedVisibleIds, selectedCampaignIds, updateSelection, isUpdating, isLoading]);

  return (
    <>
      {isLoading ? (
        <CommandSelectLoading>Loading campaigns...</CommandSelectLoading>
      ) : (
        <>
          {/* Select All/Deselect All */}
          {filteredCampaigns.length > 0 && (
            <CommandSelectAll
              checked={selectedVisibleIds.length === visibleCampaignIds.length && visibleCampaignIds.length > 0}
              indeterminate={selectedVisibleIds.length > 0 && selectedVisibleIds.length < visibleCampaignIds.length}
              disabled={isLoading || isUpdating}
              onToggle={wrappedHandleSelectAll}
              count={filteredCampaigns.length}
            />
          )}

          {sortedCampaigns.length > 0 ? (
            sortedCampaigns.map((campaign) => (
              <CommandSelectItem
                key={campaign.id}
                value={campaign.id}
                onSelect={() => handleSelectCampaign(campaign.id)}
                selected={selectedCampaignIds.includes(campaign.id)}
                disabled={isLoading || isUpdating}
              >
                <div className="flex items-center space-x-2">
                  <span className="flex-1 truncate">{campaign.name}</span>
                  {campaign.hasPaidPartnershipAds && (
                    <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">Partnership</span>
                  )}
                </div>
              </CommandSelectItem>
            ))
          ) : (
            <CommandSelectEmpty>No campaigns found.</CommandSelectEmpty>
          )}
        </>
      )}
    </>
  );
};

export const AdCampaignsOptions = ({
  adCampaigns = [],
  isLoading = false,
  value,
  onChange,
  areCampaignsEnabled = true,
}: {
  adCampaigns?: AdCampaign[];
  isLoading?: boolean;
  value: string[];
  onChange: (ids: string[]) => void;
  areCampaignsEnabled?: boolean;
}) => {
  // Local state management to prevent infinite loops (same pattern as AdAccountsOptions)
  const [selectedCampaignIds, setSelectedCampaignIds] = React.useState<string[]>(value || []);
  const [isUpdating, setIsUpdating] = React.useState(false);

  // Sync selectedCampaignIds with value prop (for form initialization and updates)
  React.useEffect(() => {
    if (!isUpdating) {
      // Only update if arrays are actually different (deep equality check)
      const arraysAreEqual =
        selectedCampaignIds.length === (value || []).length &&
        selectedCampaignIds.every((id, index) => id === (value || [])[index]);

      if (!arraysAreEqual) {
        setSelectedCampaignIds(value || []);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isUpdating]); // selectedCampaignIds intentionally excluded to prevent infinite loop

  // Unified update handler with proper state management
  const updateSelection = React.useCallback(
    (newSelection: string[]) => {
      setIsUpdating(true);
      setSelectedCampaignIds(newSelection);
      onChange(newSelection);
    },
    [onChange],
  );

  // Reset isUpdating flag when state synchronization is complete
  // This replaces setTimeout to avoid race conditions and improve testability
  React.useEffect(() => {
    if (isUpdating) {
      // Check if local state matches prop value (state has settled)
      const arraysAreEqual =
        selectedCampaignIds.length === (value || []).length &&
        selectedCampaignIds.every((id, index) => id === (value || [])[index]);

      if (arraysAreEqual) {
        setIsUpdating(false);
      }
    }
  }, [isUpdating, selectedCampaignIds, value]);

  const handleSelectCampaign = React.useCallback(
    (campaignId: string) => {
      if (isUpdating) return;

      const isCurrentlySelected = selectedCampaignIds.includes(campaignId);

      if (isCurrentlySelected) {
        // Remove from selected
        const newSelection = selectedCampaignIds.filter((id) => id !== campaignId);
        updateSelection(newSelection);
      } else {
        // Add to selected
        const newSelection = [...selectedCampaignIds, campaignId];
        updateSelection(newSelection);
      }
    },
    [selectedCampaignIds, updateSelection, isUpdating],
  );

  const displayText = React.useMemo(() => {
    // Handle disabled state for waterfall filtering
    if (!areCampaignsEnabled) {
      return { mainText: 'Please select ad accounts first...', count: null };
    }

    if (selectedCampaignIds.length === 0) return { mainText: 'Select campaigns...', count: null };
    if (selectedCampaignIds.length === 1) {
      const selectedCampaign = adCampaigns.find((campaign) => campaign.id === selectedCampaignIds[0]);
      return { mainText: selectedCampaign?.name || 'Campaign Selected', count: null };
    }
    const firstCampaign = adCampaigns.find((campaign) => campaign.id === selectedCampaignIds[0]);
    return { mainText: firstCampaign?.name || 'Campaigns Selected', count: selectedCampaignIds.length - 1 };
  }, [selectedCampaignIds, adCampaigns, areCampaignsEnabled]);

  const handleDismiss = React.useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      updateSelection([]);
    },
    [updateSelection],
  );

  return (
    <CommandSelect>
      <CommandSelectTrigger
        placeholder={areCampaignsEnabled ? 'Select campaigns...' : 'Please select ad accounts first...'}
        disabled={isUpdating || !areCampaignsEnabled}
      >
        <CommandSelectValue
          placeholder={areCampaignsEnabled ? 'Select campaigns...' : 'Please select ad accounts first...'}
          displayText={selectedCampaignIds.length > 0 ? displayText.mainText : undefined}
          count={displayText.count}
          onClear={areCampaignsEnabled ? handleDismiss : undefined}
          disabled={isUpdating || !areCampaignsEnabled}
        />
      </CommandSelectTrigger>
      {areCampaignsEnabled && (
        <CommandSelectContent searchPlaceholder="Search campaigns..." maxHeight="300px">
          <AdCampaignsContent
            adCampaigns={adCampaigns}
            isLoading={isLoading}
            selectedCampaignIds={selectedCampaignIds}
            isUpdating={isUpdating}
            handleSelectCampaign={handleSelectCampaign}
            updateSelection={updateSelection}
          />
        </CommandSelectContent>
      )}
    </CommandSelect>
  );
};
